import React from "react";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import baseURL from "../../Url/NodeBaseURL";
import OrderInvoice from "./templates/OrderInvoice";
import {
  amountToWords,
  buildPartyBlock,
  calculatePaymentReceived,
  defaultCompanyInfo,
  formatCurrency,
  formatDate,
  isSameState,
  parseNumber,
} from "./utils/common";

const fetchOrderDetails = async (orderNumber) => {
  if (!orderNumber) {
    throw new Error("Order number is required");
  }

  const response = await axios.get(
    `${baseURL}/get-order-details/${orderNumber}`
  );
  return response.data;
};

const fetchProducts = async () => {
  const response = await axios.get(`${baseURL}/get/products`);
  return Array.isArray(response.data) ? response.data : [];
};

const fetchUrdPurchases = async (estimateNumber) => {
  if (!estimateNumber) return { purchases: [], totalAmount: 0 };
  try {
    const response = await axios.get(`${baseURL}/get/olditems/${estimateNumber}`);
    if (!Array.isArray(response.data)) {
      return { purchases: [], totalAmount: 0 };
    }
    const totalAmount = response.data.reduce((sum, item) => {
      return sum + parseNumber(item.total_amount || 0);
    }, 0);
    return { purchases: response.data, totalAmount };
  } catch (error) {
    console.warn("URD purchase fetch failed:", error);
    return { purchases: [], totalAmount: 0 };
  }
};

const fetchSalesReturns = async (estimateNumber) => {
  if (!estimateNumber) return { returns: [], totalAmount: 0 };
  try {
    const response = await axios.get(`${baseURL}/get-estimate-sale-returns/${estimateNumber}`);
    if (!Array.isArray(response.data)) {
      return { returns: [], totalAmount: 0 };
    }
    const totalAmount = response.data.reduce((sum, item) => {
      const totalPrice = parseNumber(item.total_price) || 0;
      const taxAmt = parseNumber(item.tax_amt) || 0;
      const taxable = parseNumber(
        item.salesTaxableAmount ||
        item.sales_taxable_amt ||
        item.piece_taxable_amt ||
        item.taxable_amount ||
        0
      );
      const hm = parseNumber(item.hm_charges) || 0;
      const net = totalPrice > 0 ? totalPrice - hm : taxable + taxAmt - hm;
      return sum + net;
    }, 0);
    return { returns: response.data, totalAmount };
  } catch (error) {
    console.warn("Sales return fetch failed:", error);
    return { returns: [], totalAmount: 0 };
  }
};

const buildHsnMap = (products) =>
  products.reduce((map, product) => {
    if (product?.product_name) {
      map[product.product_name] = product.hsn_code || "";
    }
    if (product?.category) {
      map[product.category] = product.hsn_code || "";
    }
    return map;
  }, {});

const filterOrderLines = (orderData) =>
  (orderData?.repeatedData || []).filter(
    (item) => item.transaction_status === "Orders"
  );

const parseAdvanceAmount = (advanceAmount) => {
  if (!advanceAmount) return 0;

  try {
    if (typeof advanceAmount === "string") {
      const parsed = JSON.parse(advanceAmount);
      if (Array.isArray(parsed)) {
        return parsed.reduce(
          (sum, entry) => sum + Number(entry?.amount || entry || 0),
          0
        );
      }
      return Number(parsed) || 0;
    }

    if (Array.isArray(advanceAmount)) {
      return advanceAmount.reduce(
        (sum, entry) => sum + Number(entry?.amount || entry || 0),
        0
      );
    }

    return Number(advanceAmount) || 0;
  } catch (error) {
    console.warn("Failed to parse advance amount:", error);
    return 0;
  }
};

const collectTotals = (orderDetails) =>
  orderDetails.reduce(
    (totals, item) => ({
      rateAmount: totals.rateAmount + parseNumber(item.rate_amt),
      makingCharges: totals.makingCharges + parseNumber(item.making_charges),
      stonePrice: totals.stonePrice + parseNumber(item.stone_price),
      hmCharges: totals.hmCharges + parseNumber(item.hm_charges),
      discount: totals.discount + parseNumber( item.disscount || item.discount_amount || 0),
      festivalOffer:totals.festivalOffer + parseNumber(item.festival_discount),
      taxAmount: totals.taxAmount + parseNumber(item.tax_amt),
    }),
    {
      rateAmount: 0,
      makingCharges: 0,
      stonePrice: 0,
      hmCharges: 0,
      discount: 0,
      festivalOffer:0,
      taxAmount: 0,
    }
  );

const buildTaxRows = (taxAmount, subtotal, orderInfo) => {
  if (!taxAmount) return [];

  const percent =
    subtotal && subtotal !== 0
      ? ((taxAmount / subtotal) * 100).toFixed(2)
      : parseNumber(orderInfo.tax_percent);

  if (isSameState(orderInfo.state, orderInfo.state_code)) {
    const halfAmount = taxAmount / 2;
    const halfPercent = parseNumber(percent) / 2 || 0;
    return [
      { label: "CGST", percent: halfPercent, amount: formatCurrency(halfAmount) },
      { label: "SGST", percent: halfPercent, amount: formatCurrency(halfAmount) },
    ];
  }

  return [
    {
      label: "IGST",
      percent: parseNumber(percent),
      amount: formatCurrency(taxAmount),
    },
  ];
};

const buildItemRows = (orderDetails, hsnMap, fallbackTaxPercent = 0) =>
  orderDetails.map((item, index) => {
    const descriptionParts = [
      item.metal_type || item.metal,
      item.product_name,
    ].filter(Boolean);
    let description = descriptionParts.join(" - ") || "Item";
    if (item.client_visibility === "Client Facing" && item.remarks) {
      description += ` (${item.remarks})`;
    }

    // Format MC display
    let mcDisplay = "-";
    if (item.mc_per_gram != null && item.mc_per_gram !== "") {
      const metalType = (item.metal_type || item.metal || "").toLowerCase();
      if (["gold", "diamond", "others"].includes(metalType)) {
        mcDisplay = `${parseFloat(item.mc_per_gram).toFixed(2)} %`;
      } else {
        mcDisplay = parseFloat(item.mc_per_gram).toFixed(2);
      }
    }

    // Determine status
    let status = "Not Delivered";
    if (item.reservation_status) {
      status = item.reservation_status;
      if (item.order_status === "In Progress") {
        status += "\nNot Delivered";
      } else if (item.order_status) {
        status += `\n${item.order_status}`;
      }
    } else if (item.order_status === "In Progress") {
      status = "Not Delivered";
    } else if (item.order_status) {
      status = item.order_status;
    }

    return {
      sno: index + 1,
      description,
      hsn: hsnMap[item.product_name] || hsnMap[item.category] || "-",
      qty: parseNumber(item.qty || 1).toFixed(2),
      purity: item.printing_purity || item.purity || "-",
      grossWeight: item.gross_weight ? parseFloat(item.gross_weight).toFixed(3) : "-",
      stoneWeight: item.stone_weight ? parseFloat(item.stone_weight).toFixed(3) : "-",
      netWeight: item.total_weight_av ? parseFloat(item.total_weight_av).toFixed(3) : "-",
      rate: formatCurrency(item.rate || item.pieace_cost || item.rate_amt),
      vaPercent: item.va_percent || item.va_perc || item.wastage_percent ? parseFloat(item.va_percent || item.va_perc || item.wastage_percent).toFixed(2) : "-",
      mc: mcDisplay,
      stoneAmount: formatCurrency(parseNumber(item.stone_price)),
      status: status,
      amount: formatCurrency(
        parseNumber(item.rate_amt) +
            parseNumber(item.making_charges) +
            parseNumber(item.stone_price) +
            parseNumber(item.hm_charges || 0) ||
        parseNumber(item.total_price) 
      ),
    };
  });

const buildOrderDocument = ({ orderInfo, orderDetails, hsnMap, totals, oldItemsAmount = 0, salesReturnAmount = 0, schemeAmount = 0, festivalOfferAmount = 0 }) => {
  const taxableAmount =
    parseNumber(orderInfo.taxable_amount) ||
    totals.rateAmount + totals.makingCharges + totals.stonePrice + totals.hmCharges;
  const taxAmount =
    parseNumber(orderInfo.tax_amt) ||
    parseNumber(orderInfo.tax_amount) ||
    totals.taxAmount;
  const netAmount =
    parseNumber(orderInfo.net_amount) ||
    parseNumber(orderInfo.net_bill_amount) ||
    taxableAmount + taxAmount;

  const advancePaid = parseAdvanceAmount(orderInfo.advance_amt);
  const otherPayments = calculatePaymentReceived(
    orderInfo.cash_amount,
    orderInfo.card_amt,
    orderInfo.chq_amt,
    orderInfo.online_amt,
    orderInfo.receipts_amt
  );
  const paymentMade = otherPayments + advancePaid;

  // Calculate CGST and SGST
  const CGST = taxAmount / 2;
  const SGST = taxAmount / 2;

  return (
    <OrderInvoice
      headerTitle="Sales Order"
      company={defaultCompanyInfo}
      invoiceMeta={{
        number: orderInfo.order_number || "N/A",
        invoiceDate: (orderInfo.date),
        terms: orderInfo.terms || "Custom",
        dueDate: formatDate(orderInfo.due_date),
        placeOfSupply:
          orderInfo.place_of_supply ||
          orderInfo.state ||
          defaultCompanyInfo.placeOfSupply,
      }}
      billTo={buildPartyBlock(orderInfo)}
      items={buildItemRows(orderDetails, hsnMap, orderInfo.tax_percent)}
      totals={{//
        subtotal: formatCurrency(taxableAmount - (totals.festivalOffer || 0)),
        discount: totals.discount > 0 ? formatCurrency(totals.discount) : null,
        //festivalOffer: festivalOfferAmount > 0 ? formatCurrency(festivalOfferAmount) : null,
        festivalOffer: totals.festivalOffer > 0 ? formatCurrency(totals.festivalOffer) : null,
        taxRows: [
          { label: "SGST", percent: parseNumber((SGST / taxableAmount) * 100).toFixed(2), amount: formatCurrency(SGST) },
          { label: "CGST", percent: parseNumber((CGST / taxableAmount) * 100).toFixed(2), amount: formatCurrency(CGST) },
        ],
        total: formatCurrency(netAmount- (totals.festivalOffer || 0)),
        oldItemsAmount: oldItemsAmount > 0 ? formatCurrency(oldItemsAmount) : null,
        schemeAmount: schemeAmount > 0 ? formatCurrency(schemeAmount) : null,
        salesReturnAmount: salesReturnAmount > 0 ? formatCurrency(salesReturnAmount) : null,
        advanceAmount: advancePaid > 0 ? formatCurrency(advancePaid) : null,
        netPayableAmount: formatCurrency(Math.max(netAmount - oldItemsAmount - schemeAmount - salesReturnAmount - (totals.festivalOffer || 0)- advancePaid , 0)),
        paymentMade: formatCurrency(paymentMade),
        balanceDue: formatCurrency(Math.max(netAmount - oldItemsAmount - schemeAmount - salesReturnAmount - advancePaid - (totals.festivalOffer || 0) - paymentMade, 0)),
      }}
      amountInWords={amountToWords(netAmount)}
      notes={orderInfo.remarks || "Thank you for your order!"}
      signature="Authorized Signature"
    />
  );
};

export const generateOrderPdf = async (
  orderNumber,
  { openInNewTab = true, autoPrint = false } = {}
) => {
  if (!orderNumber) {
    throw new Error("Order number is required to generate PDF");
  }

  const [orderData, products] = await Promise.all([
    fetchOrderDetails(orderNumber),
    fetchProducts(),
  ]);

  const orderDetails = filterOrderLines(orderData);
  if (!orderDetails.length) {
    throw new Error("No order lines available to generate PDF");
  }

  const orderInfo =
    orderData.uniqueData ||
    orderData.order ||
    orderData.orderDetails ||
    orderDetails[0] ||
    {};

  const estimateNumber = orderInfo.estimate_number || orderInfo.estimateNumber || orderInfo.order_number;

  // Fetch URD purchases and sales returns
  const [{ totalAmount: oldItemsAmount }] = await Promise.all([
    fetchUrdPurchases(estimateNumber),
    // fetchSalesReturns(estimateNumber),
  ]);

  // Get scheme amount and festival offer amount from order info if available
  const schemeAmount = parseNumber(orderInfo.scheme_amount || orderInfo.schemeAmount || 0);
  const salesReturnAmount = parseNumber(orderInfo.sale_return_amt || orderInfo.sale_return_amt || 0);
  const festivalOfferAmount = parseNumber(orderInfo.festival_discount_amt || orderInfo.festivalDiscountAmt || 0);

  const document = buildOrderDocument({
    orderInfo,
    orderDetails,
    hsnMap: buildHsnMap(products),
    totals: collectTotals(orderDetails),
    oldItemsAmount,
    salesReturnAmount,
    schemeAmount,
    festivalOfferAmount,
  });

  const blob = await pdf(document).toBlob();
  const fileName = `Order-${orderNumber}.pdf`;

  if (openInNewTab && typeof window !== "undefined") {
    const pdfUrl = URL.createObjectURL(blob);
    const newWindow = window.open(pdfUrl, "_blank");
    if (newWindow && autoPrint) {
      newWindow.onload = () => {
        newWindow.focus();
        newWindow.print();
      };
    }
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 2000);
  }

  return { blob, fileName };
};

export default generateOrderPdf;

